import { useState, useRef, useCallback, useEffect } from "react";

/**
 * usePullToRefresh — detects pull-down gesture and triggers onRefresh.
 * Falls back to window.location.reload() when no callback provided.
 *
 * Ported from frontend-bk/erp/src/hooks/usePullToRefresh.js.
 */
export function usePullToRefresh(
  containerRef: React.RefObject<HTMLElement | null>,
  onRefresh?: () => void | Promise<void>,
): {
  pulling: boolean;
  pullDistance: number;
  threshold: number;
  refreshing: boolean;
} {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef(0);
  const isPulling = useRef(false);
  // Keep latest callback in a ref to avoid stale closure in event handlers.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const THRESHOLD = 80;

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const el = containerRef?.current;
      const scrollTop = el ? el.scrollTop : window.scrollY;
      if (scrollTop <= 0 && e.touches[0]) {
        startY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    },
    [containerRef],
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling.current) return;
    if (!e.touches[0]) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    if (diff > 0) {
      const distance = Math.min(diff * 0.5, 150);
      setPullDistance(distance);
      setPulling(true);
      if (distance > 10) {
        e.preventDefault();
      }
    } else {
      isPulling.current = false;
      setPulling(false);
      setPullDistance(0);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isPulling.current && pullDistance >= THRESHOLD) {
      setPullDistance(THRESHOLD);
      setRefreshing(true);
      const cb = onRefreshRef.current;
      if (typeof cb === "function") {
        Promise.resolve(cb()).finally(() => {
          setRefreshing(false);
          setPulling(false);
          setPullDistance(0);
        });
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    } else {
      setPulling(false);
      setPullDistance(0);
    }
    isPulling.current = false;
  }, [pullDistance]);

  useEffect(() => {
    const el: HTMLElement | Document =
      containerRef?.current ?? document;
    el.addEventListener("touchstart", handleTouchStart as EventListener, {
      passive: true,
    });
    el.addEventListener("touchmove", handleTouchMove as EventListener, {
      passive: false,
    });
    el.addEventListener("touchend", handleTouchEnd as EventListener, {
      passive: true,
    });

    return () => {
      el.removeEventListener(
        "touchstart",
        handleTouchStart as EventListener,
      );
      el.removeEventListener("touchmove", handleTouchMove as EventListener);
      el.removeEventListener("touchend", handleTouchEnd as EventListener);
    };
  }, [containerRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pulling, pullDistance, threshold: THRESHOLD, refreshing };
}
